import { CampusSelect } from '@/components/common/input/campus';
import { GroupSelect } from '@/components/common/input/group';
import { getGroupAlias } from '../structure/utils';
import { getFirstName, getLastName } from '@/shared/lib/names';
import { MemberStatusSelect } from '@/components/common/input/memberstatus';
import { GenderSelect } from '@/components/common/input/gender';
import { type CalendarDate, parseDate } from '@internationalized/date';
import type { Gender, Person } from '@/store/people/types';
import { DatePicker, Input } from '@/components/ui';
import type { GroupTypeKey } from '@/shared/types/group';
import type { MaritalStatusCode } from '@/lib/maritalstatus';
import { MaritalStatusSelect } from '@/components/common/input/martialstatus';
import type { Address } from '@/shared/types/address';

const DefaultLabels: Record<PersonFieldKey, string> = {
  firstname: 'First Name',
  lastname: 'Last Name',
  fullname: 'Full Name',
  preferredname: 'Preferred Name',
  gender: 'Gender',
  birthdate: 'Birthdate',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  campus_id: 'Campus',
  member_status: 'Member Status',
  marital_status: 'Marital Status',
  laygroup1: getGroupAlias('laygroup1', 'type'),
  laygroup2: getGroupAlias('laygroup2', 'type'),
};

export type PersonFieldKey =
  | keyof Pick<
      Person['data'],
      | 'firstname'
      | 'lastname'
      | 'fullname'
      | 'preferredname'
      | 'gender'
      | 'birthdate'
      | 'email'
      | 'phone'
      | 'address'
      | 'campus_id'
      | 'member_status'
      | 'marital_status'
    >
  | 'laygroup1'
  | 'laygroup2';

export type FieldInteractions = Partial<
  Record<PersonFieldKey, FieldInteraction>
>;

export interface FieldInteraction {
  label?: string;
  defaultLabel?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}

export function FieldDisplay({
  editable,
  fieldKey,
  interaction,
  onStringChange,
  onGenderChange,
  onBirthdateChange,
  onCampusChange,
  onLayGroupChange,
  onAddressChange,
}: {
  editable: Person;
  fieldKey: PersonFieldKey;
  interaction?: FieldInteraction;
  onStringChange: (key: PersonFieldKey, value: string) => void;
  onGenderChange: (key: PersonFieldKey, value: Gender | undefined) => void;
  onBirthdateChange: (key: PersonFieldKey, value: string) => void;
  onCampusChange: (id: string) => void;
  onLayGroupChange: (id: string, level: GroupTypeKey) => void;
  onAddressChange?: (address: Partial<Address>) => void;
}) {
  const initialValue = getInitialValue(editable, fieldKey);

  const birthdate =
    fieldKey !== 'birthdate' || !initialValue
      ? undefined
      : parseDate(initialValue.slice(0, 10));
  const setBirthdate = (value: CalendarDate | null) => {
    if (!value) {
      onBirthdateChange(fieldKey, '');
    } else {
      onBirthdateChange(
        fieldKey,
        value.toDate('UTC').toISOString().substring(0, 10),
      );
    }
  };

  const setMaritalStatus = (value: MaritalStatusCode) => {
    onStringChange('marital_status', value);
  };

  const label =
    interaction?.label ||
    (interaction?.defaultLabel ||
    typeof interaction?.defaultLabel === 'undefined'
      ? DefaultLabels[fieldKey]
      : undefined);

  switch (fieldKey) {
    case 'firstname':
    case 'lastname':
    case 'preferredname':
    case 'email':
    case 'phone':
      return (
        <Input
          label={label}
          value={initialValue ?? ''}
          onValueChange={(value: string) => onStringChange(fieldKey, value)}
          readOnly={interaction?.isReadOnly}
        />
      );
    case 'gender':
      return (
        <GenderSelect
          label={label}
          value={initialValue as Gender | undefined}
          onChange={(value) => onGenderChange(fieldKey, value)}
          isDisabled={interaction?.isDisabled}
          isReadOnly={interaction?.isReadOnly}
        />
      );
    case 'birthdate':
      return (
        <DatePicker
          label={label}
          showMonthAndYearPickers
          value={birthdate}
          onChange={setBirthdate}
          isReadOnly={interaction?.isReadOnly}
          isDisabled={interaction?.isDisabled}
        />
      );
    case 'marital_status':
      return (
        <MaritalStatusSelect
          label={label}
          value={initialValue as MaritalStatusCode}
          onSingleChange={setMaritalStatus}
          isReadOnly={interaction?.isReadOnly}
        />
      );
    case 'campus_id':
      return (
        <CampusSelect
          label={label}
          value={initialValue}
          isDisabled={interaction?.isDisabled}
          isReadOnly={interaction?.isReadOnly}
          onChangeSingle={onCampusChange}
        />
      );
    case 'member_status':
      return (
        <MemberStatusSelect
          label={label}
          selected={initialValue}
          onChange={(value: string | string[] | undefined) => {
            onStringChange(fieldKey, value as string);
          }}
          isReadOnly={interaction?.isReadOnly}
        />
      );
    case 'laygroup1':
      return (
        <GroupSelect
          type={'laygroup1'}
          label={label}
          value={initialValue}
          onChangeSingle={(id: string) => onLayGroupChange(id, 'laygroup1')}
          isDisabled={interaction?.isDisabled}
          isReadOnly={interaction?.isReadOnly}
        />
      );
    case 'laygroup2':
      return (
        <GroupSelect
          type={'laygroup2'}
          label={label}
          value={initialValue}
          parent={editable?.data.groups?.laygroup1}
          onChangeSingle={(id: string) => onLayGroupChange(id, 'laygroup2')}
          isDisabled={interaction?.isDisabled}
          isReadOnly={interaction?.isReadOnly}
        />
      );
    case 'address':
      return (
        <AddressFields
          label={label}
          address={editable.data.address}
          onChange={onAddressChange}
          isReadOnly={interaction?.isReadOnly}
          isDisabled={interaction?.isDisabled}
        />
      );
    default:
      // fullname
      return null;
  }
}

function getInitialValue(target: Person, fieldKey: PersonFieldKey) {
  if (fieldKey === 'laygroup1' || fieldKey === 'laygroup2') {
    return target.data.groups?.[fieldKey];
  }

  if (fieldKey === 'firstname') {
    return getFirstName(target.data);
  }

  if (fieldKey === 'lastname') {
    return getLastName(target.data);
  }

  return target.data[fieldKey];
}

function AddressFields({
  label,
  address,
  onChange,
  isReadOnly,
  isDisabled,
}: {
  label?: string;
  address?: Address;
  onChange?: (address: Partial<Address>) => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}) {
  const handleFieldChange = (field: keyof Address, value: string) => {
    onChange?.({ ...address, [field]: value });
  };

  return (
    <div className='flex flex-col gap-2'>
      {label && (
        <span className='text-sm font-medium text-default-700'>{label}</span>
      )}
      {/* Street */}
      <Input
        label='Street'
        value={address?.street ?? ''}
        onValueChange={(value) => handleFieldChange('street', value)}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
      />
      {/* Line 2 */}
      <Input
        label='Line 2'
        value={address?.line2 ?? ''}
        onValueChange={(value) => handleFieldChange('line2', value)}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
      />
      {/* City, State, Zip */}
      <div className='grid grid-cols-6 gap-2'>
        <div className='col-span-3'>
          <Input
            label='City'
            value={address?.city ?? ''}
            onValueChange={(value) => handleFieldChange('city', value)}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
          />
        </div>
        <div className='col-span-1'>
          <Input
            label='State'
            value={address?.state ?? ''}
            onValueChange={(value) => handleFieldChange('state', value)}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
          />
        </div>
        <div className='col-span-2'>
          <Input
            label='Zip'
            value={address?.zip ?? ''}
            onValueChange={(value) => handleFieldChange('zip', value)}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
          />
        </div>
      </div>
      {/* Country */}
      <Input
        label='Country'
        value={address?.country ?? ''}
        onValueChange={(value) => handleFieldChange('country', value)}
        isReadOnly={isReadOnly}
        isDisabled={isDisabled}
      />
    </div>
  );
}
